import { TFunction } from 'i18next';
import { PackageTenantStates } from 'models/package';
import { IntegrationGroup, IntegrationGroupStates } from './integrationGroup';
import { IntegrationTypeHandler } from './integrationTypeHandler';

export enum IntegrationGroups {
  NotSet = 0,
  MS_MFA = 1,
  MS_SecureScores = 2,
  SslLabs = 3,
  //the Microsoft Entra ID baseline checks (API Docs/MS_EntraBaseline.md); 4 is SkillControl, which the store never offers
  MS_EntraBaseline = 5,
  HubSpot = 6,
  //the Microsoft Azure checks (API Docs/MS_Azure.md); shown in the Store only while the preview toggle is on
  MS_Azure = 7,
  //the GitHub checks (API Docs/GitHub_Baseline.md), read through the ISOPlanner GitHub App
  GitHub = 8,
}

export class IntegrationGroupHandler {
  integrationGroupId: IntegrationGroups;

  name: string;

  shortDescription: string;

  description: string;

  image: string;

  integrationTypes: IntegrationTypeHandler[];

  integrationGroup?: IntegrationGroup;

  constructor() {
    this.integrationGroupId = IntegrationGroups.NotSet;
    this.name = '';
    this.description = '';
    this.shortDescription = '';
    this.image = '';
    this.integrationTypes = [];
  }

  clone(): IntegrationGroupHandler {
    const clone = new IntegrationGroupHandler();
    clone.integrationGroupId = this.integrationGroupId;
    clone.integrationTypes = this.integrationTypes.map((i) => i.clone());
    clone.name = this.name;
    clone.shortDescription = this.shortDescription;
    clone.description = this.description;
    clone.image = this.image;
    clone.integrationGroup = this.integrationGroup;

    return clone;
  }

  //An app with any check added has a group row and counts as activated, also when other checks are not added.
  getPackageState(): PackageTenantStates {
    return this.integrationGroup ? PackageTenantStates.Activated : PackageTenantStates.Available;
  }
}

//Buckets the catalogue types under their app using the group id the API returns on the type row, and reads the
//group state from the first added type, because the API nests the group on every integration of the group.
export function assignIntegrationTypesToGroups(
  groups: IntegrationGroupHandler[],
  integrationTypes: IntegrationTypeHandler[],
): IntegrationGroupHandler[] {
  for (const groupHandler of groups) {
    groupHandler.integrationTypes = integrationTypes.filter(
      (it) => it.integrationType?.integrationGroupId === groupHandler.integrationGroupId,
    );
    const firstWithGroup = groupHandler.integrationTypes.find((it) => it.integration?.integrationGroup);
    groupHandler.integrationGroup = firstWithGroup?.integration?.integrationGroup;
  }

  return groups;
}

//The checks without an integration of every app the tenant is subscribed to (its group row in Trial or Active), so
//a check added to an app later reaches every subscribed tenant. The app is the group id the API returns on the type
//row and its state the group the API nests on the integrations; an app without a group row or an Inactive one is
//skipped, and so is a catalogue type the API has no row for yet.
export function getMissingIntegrationTypesOfSubscribedGroups(
  integrationTypes: IntegrationTypeHandler[],
): IntegrationTypeHandler[] {
  const subscribedGroupIds = getSubscribedIntegrationGroupIds(integrationTypes);

  return integrationTypes.filter(
    (it) => !it.integration && it.integrationType && subscribedGroupIds.has(it.integrationType.integrationGroupId),
  );
}

//True when the check belongs to an app the tenant is subscribed to: such a check is disabled, never removed, because
//the scene adds a missing check of a subscribed app again (getMissingIntegrationTypesOfSubscribedGroups).
export function isIntegrationTypeOfSubscribedGroup(
  integrationType: IntegrationTypeHandler,
  integrationTypes: IntegrationTypeHandler[],
): boolean {
  if (!integrationType.integrationType) return false;

  return getSubscribedIntegrationGroupIds(integrationTypes).has(integrationType.integrationType.integrationGroupId);
}

//The apps with a group row in Trial or Active, read from the group the API nests on the integrations.
function getSubscribedIntegrationGroupIds(integrationTypes: IntegrationTypeHandler[]): Set<number> {
  const subscribedGroupIds = new Set<number>();
  for (const it of integrationTypes) {
    const state = it.integration?.integrationGroup?.state;
    if (it.integrationType && (state === IntegrationGroupStates.Trial || state === IntegrationGroupStates.Active)) {
      subscribedGroupIds.add(it.integrationType.integrationGroupId);
    }
  }

  return subscribedGroupIds;
}

export function getIntegrationGroups(t: TFunction<string[]>) {
  const integrationGroups: IntegrationGroupHandler[] = [];

  const igh_MS_MFA = new IntegrationGroupHandler();
  igh_MS_MFA.integrationGroupId = IntegrationGroups.MS_MFA;
  igh_MS_MFA.name = t('integrations:Groups.MS_MFA.Name');
  igh_MS_MFA.shortDescription = t('integrations:Groups.MS_MFA.ShortDescription');
  igh_MS_MFA.description = t('integrations:Groups.MS_MFA.Description');
  igh_MS_MFA.image = 'ms_mfa.png';
  integrationGroups.push(igh_MS_MFA);

  const igh_MS_SecureScores = new IntegrationGroupHandler();
  igh_MS_SecureScores.integrationGroupId = IntegrationGroups.MS_SecureScores;
  igh_MS_SecureScores.name = t('integrations:Groups.MS_SecureScores.Name');
  igh_MS_SecureScores.shortDescription = t('integrations:Groups.MS_SecureScores.ShortDescription');
  igh_MS_SecureScores.description = t('integrations:Groups.MS_SecureScores.Description');
  igh_MS_SecureScores.image = 'ms_securescores.png';
  integrationGroups.push(igh_MS_SecureScores);

  const igh_SslLabs = new IntegrationGroupHandler();
  igh_SslLabs.integrationGroupId = IntegrationGroups.SslLabs;
  igh_SslLabs.name = t('integrations:Groups.SslLabs.Name');
  igh_SslLabs.shortDescription = t('integrations:Groups.SslLabs.ShortDescription');
  igh_SslLabs.description = t('integrations:Groups.SslLabs.Description');
  igh_SslLabs.image = 'ssl_labs.png';
  integrationGroups.push(igh_SslLabs);

  const igh_MS_EntraBaseline = new IntegrationGroupHandler();
  igh_MS_EntraBaseline.integrationGroupId = IntegrationGroups.MS_EntraBaseline;
  igh_MS_EntraBaseline.name = t('integrations:Groups.MS_EntraBaseline.Name');
  igh_MS_EntraBaseline.shortDescription = t('integrations:Groups.MS_EntraBaseline.ShortDescription');
  igh_MS_EntraBaseline.description = t('integrations:Groups.MS_EntraBaseline.Description');
  igh_MS_EntraBaseline.image = 'ms_entra_baseline.png';
  integrationGroups.push(igh_MS_EntraBaseline);

  const igh_HubSpot = new IntegrationGroupHandler();
  igh_HubSpot.integrationGroupId = IntegrationGroups.HubSpot;
  igh_HubSpot.name = t('integrations:Groups.HubSpot.Name');
  igh_HubSpot.shortDescription = t('integrations:Groups.HubSpot.ShortDescription');
  igh_HubSpot.description = t('integrations:Groups.HubSpot.Description');
  igh_HubSpot.image = 'hubspot.png';
  integrationGroups.push(igh_HubSpot);

  const igh_MS_Azure = new IntegrationGroupHandler();
  igh_MS_Azure.integrationGroupId = IntegrationGroups.MS_Azure;
  igh_MS_Azure.name = t('integrations:Groups.MS_Azure.Name');
  igh_MS_Azure.shortDescription = t('integrations:Groups.MS_Azure.ShortDescription');
  igh_MS_Azure.description = t('integrations:Groups.MS_Azure.Description');
  igh_MS_Azure.image = 'ms_azure.png';
  integrationGroups.push(igh_MS_Azure);
  const igh_GitHub = new IntegrationGroupHandler();
  igh_GitHub.integrationGroupId = IntegrationGroups.GitHub;
  igh_GitHub.name = t('integrations:Groups.GitHub.Name');
  igh_GitHub.shortDescription = t('integrations:Groups.GitHub.ShortDescription');
  igh_GitHub.description = t('integrations:Groups.GitHub.Description');
  igh_GitHub.image = 'github.png';
  integrationGroups.push(igh_GitHub);

  return integrationGroups;
}
